/* ═══════════════════════════════════════════════════════════════
   SpecService — Git-like branching model for Spec Masters

   Published SpecMaster = main branch (never edited directly)
   SpecMasterDraft      = feature branch (local working copy)
   Publish Request      = pull request (diff review by admin)

   Flow: checkout → edit draft → save draft → submit publish → admin review → merge
   ═══════════════════════════════════════════════════════════════ */

import type {
  CsiDivision,
  SystemCatalogEntry,
  SpecMaster,
  SpecMasterDraft,
  SpecMasterVersion,
  SpecMasterStatus,
  PublishRequest,
  BlockDiff,
  ContentBlock,
  Project,
  AssembledSpec,
  SystemModule,
  Variant,
} from '../../shared/types';
import { pushEditHistory } from '../../shared/editHistory';
import { ProjectService } from './projectService';
import { SpecMasterService } from './specMasterService';

export const CSI_DIVISIONS: CsiDivision[] = [
  { code: '01', name: 'General Requirements' },
  { code: '02', name: 'Existing Conditions' },
  { code: '03', name: 'Concrete' },
  { code: '04', name: 'Masonry' },
  { code: '05', name: 'Metals' },
  { code: '06', name: 'Wood, Plastics, and Composites' },
  { code: '07', name: 'Thermal and Moisture Protection' },
  { code: '08', name: 'Openings' },
  { code: '09', name: 'Finishes' },
  { code: '10', name: 'Specialties' },
  { code: '11', name: 'Equipment' },
  { code: '12', name: 'Furnishings' },
  { code: '13', name: 'Special Construction' },
  { code: '14', name: 'Conveying Equipment' },
  { code: '21', name: 'Fire Suppression' },
  { code: '22', name: 'Plumbing' },
  { code: '23', name: 'Heating, Ventilating, and Air Conditioning' },
  { code: '25', name: 'Integrated Automation' },
  { code: '26', name: 'Electrical' },
  { code: '27', name: 'Communications' },
  { code: '28', name: 'Electronic Safety and Security' },
  { code: '31', name: 'Earthwork' },
  { code: '32', name: 'Exterior Improvements' },
  { code: '33', name: 'Utilities' },
];

/* ─── System Catalog (reference data for system types) ─── */
export const SYSTEM_CATALOG: SystemCatalogEntry[] = [
  {
    id: 'fire-protection',
    name: 'Fire Protection',
    icon: '🔥',
    description: 'Fire-rated assemblies & suppression',
  },
  { id: 'seismic', name: 'Seismic', icon: '🌊', description: 'Seismic bracing & design criteria' },
  {
    id: 'sustainability',
    name: 'Sustainability',
    icon: '🌿',
    description: 'LEED, green requirements',
  },
  {
    id: 'moisture',
    name: 'Moisture Control',
    icon: '💧',
    description: 'Vapor barriers, waterproofing',
  },
  { id: 'acoustic', name: 'Acoustic', icon: '🔇', description: 'STC/IIC ratings, sound control' },
  { id: 'security', name: 'Security', icon: '🔒', description: 'Access control, blast-resistance' },
  {
    id: 'hvac-integration',
    name: 'HVAC Integration',
    icon: '🌡️',
    description: 'Ductwork, plenums, insulation coordination',
  },
  {
    id: 'code-compliance',
    name: 'Code Compliance',
    icon: '📋',
    description: 'IBC, ADA, local jurisdiction',
  },
];

/* ═══════════════════════════════════════════════════════════════
   WORD-LEVEL DIFF — For track-changes style rendering of modified blocks.
   ═══════════════════════════════════════════════════════════════ */

export interface DiffToken {
  value: string;
  op: 'keep' | 'add' | 'del';
}

/** Token-level LCS between two text strings. Whitespace is kept as separate
 *  tokens so we don't mangle spacing in the rendered diff. */
export function diffWords(oldText: string, newText: string): DiffToken[] {
  const tokenize = (s: string): string[] => s.split(/(\s+)/).filter((x) => x.length > 0);
  const a = tokenize(oldText || '');
  const b = tokenize(newText || '');
  const m = a.length;
  const n = b.length;

  // Standard LCS DP table
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  // Backtrack
  const out: DiffToken[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      out.unshift({ value: a[i - 1], op: 'keep' });
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      out.unshift({ value: a[i - 1], op: 'del' });
      i--;
    } else {
      out.unshift({ value: b[j - 1], op: 'add' });
      j--;
    }
  }
  while (i > 0) {
    out.unshift({ value: a[--i], op: 'del' });
  }
  while (j > 0) {
    out.unshift({ value: b[--j], op: 'add' });
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════
   INLINE DIFF — Interleaved old+new blocks for track-changes rendering.
   Walks the new blocks in order, splicing in removed-from-old blocks at the
   positions they previously occupied so the rendered diff reads like the old
   document with tracked changes, not "here are additions and here are deletions
   in separate piles."
   ═══════════════════════════════════════════════════════════════ */

export type InlineDiffKind = 'unchanged' | 'added' | 'removed' | 'modified';

export interface InlineDiffBlock {
  kind: InlineDiffKind;
  id: string;
  level: number;
  oldText?: string;
  newText?: string;
  oldLevel?: number;
  newLevel?: number;
  wordDiff?: DiffToken[];
  blockType?: string;
  imageData?: string;
}

export function buildInlineDiff(
  oldBlocks: ContentBlock[],
  newBlocks: ContentBlock[]
): InlineDiffBlock[] {
  const oldIdx = new Map(oldBlocks.map((b, i) => [b.id, i]));
  const newIds = new Set(newBlocks.map((b) => b.id));
  const out: InlineDiffBlock[] = [];
  let lastOldIdxEmitted = -1;

  const pushRemoved = (b: ContentBlock): void => {
    out.push({
      kind: 'removed',
      id: b.id,
      level: b.level,
      oldText: b.text,
      oldLevel: b.level,
      blockType: b.blockType,
      imageData: b.imageData,
    });
  };

  for (const nb of newBlocks) {
    const oIdxForNb = oldIdx.get(nb.id);
    if (oIdxForNb !== undefined) {
      // Splice in any removed blocks that lived between the last anchor and this one
      for (let k = lastOldIdxEmitted + 1; k < oIdxForNb; k++) {
        const rb = oldBlocks[k];
        if (!newIds.has(rb.id)) pushRemoved(rb);
      }
      lastOldIdxEmitted = oIdxForNb;

      const ob = oldBlocks[oIdxForNb];
      if (ob.text !== nb.text || ob.level !== nb.level) {
        out.push({
          kind: 'modified',
          id: nb.id,
          level: nb.level,
          oldText: ob.text,
          newText: nb.text,
          oldLevel: ob.level,
          newLevel: nb.level,
          wordDiff: diffWords(ob.text, nb.text),
          blockType: nb.blockType,
          imageData: nb.imageData,
        });
      } else {
        out.push({
          kind: 'unchanged',
          id: nb.id,
          level: nb.level,
          oldText: ob.text,
          newText: nb.text,
          blockType: nb.blockType,
          imageData: nb.imageData,
        });
      }
    } else {
      // Added
      out.push({
        kind: 'added',
        id: nb.id,
        level: nb.level,
        newText: nb.text,
        newLevel: nb.level,
        blockType: nb.blockType,
        imageData: nb.imageData,
      });
    }
  }

  // Tail: removed blocks that lived after the last surviving new block
  for (let k = lastOldIdxEmitted + 1; k < oldBlocks.length; k++) {
    const rb = oldBlocks[k];
    if (!newIds.has(rb.id)) pushRemoved(rb);
  }

  return out;
}

/* ═══════════════════════════════════════════════════════════════
   REVISION MARKS — attach computed revision info to a block list.
   Returns the new blocks in document order with `revision` stamped on
   added/modified blocks, and removed old blocks spliced back in at the
   positions they previously occupied (kind: 'removed'). Feed the result
   to SpecFormattedView or buildSpecDocx with RevisionSettings to render
   architect-style change marks. Purely computed — nothing is persisted.
   ═══════════════════════════════════════════════════════════════ */

export function applyRevisionMarks(
  oldBlocks: ContentBlock[],
  newBlocks: ContentBlock[]
): ContentBlock[] {
  const diff = buildInlineDiff(oldBlocks, newBlocks);
  const newById = new Map(newBlocks.map((b) => [b.id, b]));
  const oldById = new Map(oldBlocks.map((b) => [b.id, b]));
  const out: ContentBlock[] = [];
  for (const d of diff) {
    if (d.kind === 'removed') {
      const ob = oldById.get(d.id);
      if (ob) out.push({ ...ob, revision: { kind: 'removed' } });
      continue;
    }
    const nb = newById.get(d.id);
    if (!nb) continue;
    if (d.kind === 'unchanged') out.push(nb);
    else out.push({ ...nb, revision: { kind: d.kind, wordDiff: d.wordDiff } });
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════
   DIFF UTILITY — Compare two sets of blocks
   ═══════════════════════════════════════════════════════════════ */
export function diffBlocks(oldBlocks: ContentBlock[], newBlocks: ContentBlock[]): BlockDiff[] {
  const oldMap = new Map(oldBlocks.map((b) => [b.id, b]));
  const newMap = new Map(newBlocks.map((b) => [b.id, b]));
  const diffs: BlockDiff[] = [];

  // Walk new blocks in order
  for (const nb of newBlocks) {
    const ob = oldMap.get(nb.id);
    if (!ob) {
      diffs.push({
        blockId: nb.id,
        level: nb.level,
        type: 'added',
        newText: nb.text,
        newLevel: nb.level,
      });
    } else if (ob.text !== nb.text || ob.level !== nb.level) {
      diffs.push({
        blockId: nb.id,
        level: nb.level,
        type: 'modified',
        oldText: ob.text,
        newText: nb.text,
        oldLevel: ob.level,
        newLevel: nb.level,
      });
    } else {
      diffs.push({
        blockId: nb.id,
        level: nb.level,
        type: 'unchanged',
        oldText: ob.text,
        newText: nb.text,
      });
    }
  }

  // Find removed blocks (in old but not in new)
  for (const ob of oldBlocks) {
    if (!newMap.has(ob.id)) {
      diffs.push({
        blockId: ob.id,
        level: ob.level,
        type: 'removed',
        oldText: ob.text,
        oldLevel: ob.level,
      });
    }
  }

  return diffs;
}

/* ═══════════════════════════════════════════════════════════════
   CHANGE SUMMARY — Systems, Block Groups, Variants comparison
   ═══════════════════════════════════════════════════════════════ */

export interface ChangeSummary {
  systemsAdded: string[];
  systemsRemoved: string[];
  systemsModified: string[];
  variantsAdded: string[];
  variantsRemoved: string[];
  variantsModified: string[];
}

export function diffChangeSummary(
  master: { systems: SystemModule[]; variants?: Variant[] },
  draft: { systems: SystemModule[]; variants?: Variant[] }
): ChangeSummary {
  const summary: ChangeSummary = {
    systemsAdded: [],
    systemsRemoved: [],
    systemsModified: [],
    variantsAdded: [],
    variantsRemoved: [],
    variantsModified: [],
  };

  // Systems
  const oldSys = new Map((master.systems || []).map((s) => [s.id, s]));
  const newSys = new Map((draft.systems || []).map((s) => [s.id, s]));
  for (const [id, ns] of newSys) {
    const os = oldSys.get(id);
    if (!os) {
      summary.systemsAdded.push(ns.name);
    } else if (os.name !== ns.name || JSON.stringify(os.content) !== JSON.stringify(ns.content)) {
      summary.systemsModified.push(ns.name);
    }
  }
  for (const [id, os] of oldSys) {
    if (!newSys.has(id)) summary.systemsRemoved.push(os.name);
  }

  // Variants
  const oldVar = new Map((master.variants || []).map((v) => [v.id, v]));
  const newVar = new Map((draft.variants || []).map((v) => [v.id, v]));
  for (const [id, nv] of newVar) {
    const ov = oldVar.get(id);
    if (!ov) {
      summary.variantsAdded.push(nv.name);
    } else if (
      ov.name !== nv.name ||
      ov.status !== nv.status ||
      ov.description !== nv.description ||
      JSON.stringify(ov.tags) !== JSON.stringify(nv.tags)
    ) {
      summary.variantsModified.push(nv.name);
    }
  }
  for (const [id, ov] of oldVar) {
    if (!newVar.has(id)) summary.variantsRemoved.push(ov.name);
  }

  return summary;
}

/* ═══════════════════════════════════════════════════════════════
   SERVICE OBJECT — All CRUD operations delegate to window.api
   ═══════════════════════════════════════════════════════════════ */
export const SpecService = {
  /* ── Utility ── */
  generateId: (): string =>
    'id-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6),
  generateProjectId: (): string =>
    'proj-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),

  /* ── Reference Data ── */
  getDivisions: (): CsiDivision[] => CSI_DIVISIONS,
  getSystemCatalog: (): SystemCatalogEntry[] => SYSTEM_CATALOG,

  /* ══════════════════════════════════════════════════════════════
     SPEC MASTERS (Published — the "main branch")
     ══════════════════════════════════════════════════════════════ */
  listMasters: async (): Promise<SpecMaster[]> => SpecMasterService.list(),

  getMaster: async (id: string): Promise<SpecMaster | null> => SpecMasterService.get(id),

  /** Direct save to published spec master (used internally for merge/admin operations) */
  savePublished: async (master: SpecMaster): Promise<SpecMaster> => {
    master.lastModified = new Date().toISOString();
    return SpecMasterService.save(master);
  },

  deleteMaster: async (id: string): Promise<void> => SpecMasterService.delete(id),

  /** Save As — duplicate with new ID, reset version to 1, clear history */
  saveAsNewMaster: async (
    source: SpecMaster,
    newTitle: string,
    userName?: string
  ): Promise<SpecMaster> => {
    const newMaster: SpecMaster = {
      ...JSON.parse(JSON.stringify(source)),
      id: SpecService.generateId(),
      title: newTitle,
      version: 1,
      status: 'draft' as SpecMasterStatus,
      versionHistory: [],
      lockedBy: undefined,
      lockedAt: undefined,
      lastModified: new Date().toISOString(),
      modifiedBy: userName || source.modifiedBy,
      editHistory: pushEditHistory(
        [],
        userName || source.modifiedBy || 'Unknown',
        undefined,
        'created'
      ),
    };
    return SpecMasterService.save(newMaster);
  },

  /* ══════════════════════════════════════════════════════════════
     DRAFTS (Working copies — "feature branches")
     ══════════════════════════════════════════════════════════════ */
  listDrafts: async (): Promise<SpecMasterDraft[]> => SpecMasterService.listDrafts(),

  getDraft: async (id: string): Promise<SpecMasterDraft | null> => SpecMasterService.getDraft(id),

  /** Get any existing draft for a spec master by a specific user */
  getDraftForBlueprint: async (
    blueprintId: string,
    userName: string
  ): Promise<SpecMasterDraft | null> => {
    const list = await SpecMasterService.listDrafts();
    return list.find((d) => d.blueprintId === blueprintId && d.editedBy === userName) || null;
  },

  /**
   * CHECKOUT — Create a draft from a published spec master and lock it.
   * Like `git checkout -b feature-branch`
   */
  checkoutBlueprint: async (
    blueprintId: string,
    userName: string
  ): Promise<SpecMasterDraft | null> => {
    const master = await SpecService.getMaster(blueprintId);
    if (!master) return null;

    // Check if already locked by someone else
    if (master.lockedBy && master.lockedBy !== userName) {
      throw new Error(`Spec Master is locked by ${master.lockedBy} since ${master.lockedAt}`);
    }

    // Check if user already has a draft
    const existing = await SpecService.getDraftForBlueprint(blueprintId, userName);
    if (existing) return existing;

    // Lock the spec master
    master.lockedBy = userName;
    master.lockedAt = new Date().toISOString();
    await SpecService.savePublished(master);

    // Create the draft
    const draft: SpecMasterDraft = {
      id: 'draft-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6),
      blueprintId: master.id,
      baseVersion: master.version,
      editedBy: userName,
      csiNumber: master.csiNumber,
      title: master.title,
      description: master.description,
      coreContent: JSON.parse(JSON.stringify(master.coreContent)),
      systems: JSON.parse(JSON.stringify(master.systems)),
      variants: JSON.parse(JSON.stringify(master.variants || [])),
      createdAt: new Date().toISOString(),
      lastSaved: new Date().toISOString(),
    };

    await SpecMasterService.saveDraft(draft);
    return draft;
  },

  /** SAVE DRAFT — Save to cloud, doesn't touch the published spec master */
  saveDraft: async (draft: SpecMasterDraft): Promise<SpecMasterDraft> => {
    draft.lastSaved = new Date().toISOString();
    return SpecMasterService.saveDraft(draft);
  },

  /** DISCARD DRAFT — Delete the draft and unlock the spec master */
  discardDraft: async (draftId: string): Promise<void> => {
    const draft = await SpecService.getDraft(draftId);
    if (!draft) return;

    // Unlock the spec master
    const master = await SpecService.getMaster(draft.blueprintId);
    if (master && master.lockedBy === draft.editedBy) {
      master.lockedBy = undefined;
      master.lockedAt = undefined;
      await SpecService.savePublished(master);
    }

    await SpecMasterService.deleteDraft(draftId);
  },

  /** FORCE OVERRIDE — Discard another user's draft and unlock the spec master */
  forceOverrideDraft: async (masterId: string): Promise<void> => {
    // Find and delete any draft for this master
    const allDrafts = await SpecService.listDrafts();
    const draftsForMaster = allDrafts.filter((d) => d.blueprintId === masterId);
    for (const d of draftsForMaster) {
      await SpecMasterService.deleteDraft(d.id);
    }

    // Force-unlock the spec master
    const master = await SpecService.getMaster(masterId);
    if (master) {
      master.lockedBy = undefined;
      master.lockedAt = undefined;
      await SpecService.savePublished(master);
    }
  },

  /**
   * SUBMIT PUBLISH REQUEST — Like opening a PR.
   * Generates a diff and attaches a PublishRequest to the draft.
   */
  submitPublishRequest: async (
    draftId: string,
    message?: string
  ): Promise<SpecMasterDraft | null> => {
    const draft = await SpecService.getDraft(draftId);
    if (!draft) return null;

    const master = await SpecService.getMaster(draft.blueprintId);
    if (!master) return null;

    draft.publishRequest = {
      id: SpecService.generateId(),
      draftId: draft.id,
      blueprintId: draft.blueprintId,
      requestedBy: draft.editedBy,
      requestedAt: new Date().toISOString(),
      message,
      status: 'pending',
    };
    draft.lastSaved = new Date().toISOString();

    await SpecMasterService.saveDraft(draft);
    return draft;
  },

  /**
   * PUBLISH (MERGE) — Admin approves and merges draft into published spec master.
   * Like merging a PR into main.
   */
  publishDraft: async (
    draftId: string,
    reviewerName: string,
    note?: string
  ): Promise<SpecMaster | null> => {
    const draft = await SpecService.getDraft(draftId);
    if (!draft) return null;

    const master = await SpecService.getMaster(draft.blueprintId);
    if (!master) return null;

    // Archive the current published version
    const maxVersions = master.maxVersionsKept || 10;
    const snapshot: SpecMasterVersion = {
      version: master.version,
      snapshot: {
        coreContent: JSON.parse(JSON.stringify(master.coreContent)),
        systems: JSON.parse(JSON.stringify(master.systems)),
        title: master.title,
        description: master.description,
      },
      savedBy: master.modifiedBy || 'unknown',
      savedAt: master.lastModified,
    };
    const history = [...(master.versionHistory || []), snapshot];
    master.versionHistory = history.slice(-maxVersions);

    // Merge draft content into published
    master.coreContent = JSON.parse(JSON.stringify(draft.coreContent));
    master.systems = JSON.parse(JSON.stringify(draft.systems));
    master.variants = JSON.parse(JSON.stringify(draft.variants || []));
    master.title = draft.title;
    master.description = draft.description;
    master.csiNumber = draft.csiNumber;
    master.version = (master.version || 0) + 1;
    master.modifiedBy = draft.editedBy;
    master.status = 'published';
    master.lastModified = new Date().toISOString();
    master.editHistory = pushEditHistory(
      master.editHistory,
      draft.editedBy,
      undefined,
      'published'
    );
    // Unlock
    master.lockedBy = undefined;
    master.lockedAt = undefined;

    await SpecService.savePublished(master);

    // Delete the draft
    await SpecMasterService.deleteDraft(draftId);

    return master;
  },

  /** REJECT PUBLISH — Admin rejects the PR. Draft stays for editing. */
  rejectPublish: async (
    draftId: string,
    reviewerName: string,
    note?: string
  ): Promise<SpecMasterDraft | null> => {
    const draft = await SpecService.getDraft(draftId);
    if (!draft || !draft.publishRequest) return null;

    draft.publishRequest.status = 'rejected';
    draft.publishRequest.reviewedBy = reviewerName;
    draft.publishRequest.reviewedAt = new Date().toISOString();
    draft.publishRequest.reviewNote = note;
    draft.lastSaved = new Date().toISOString();

    await SpecMasterService.saveDraft(draft);
    return draft;
  },

  /**
   * DIRECT PUBLISH — Admin skips the PR workflow and publishes immediately.
   * Checkout → edit → direct publish in one step.
   */
  directPublish: async (draft: SpecMasterDraft, userName: string): Promise<SpecMaster | null> => {
    // Save the draft first
    draft.lastSaved = new Date().toISOString();
    await SpecMasterService.saveDraft(draft);
    // Then merge it
    return await SpecService.publishDraft(draft.id, userName);
  },

  /** Revert to a previous version (creates a new version) */
  revertMaster: async (
    id: string,
    targetVersion: number,
    userName?: string
  ): Promise<SpecMaster | null> => {
    const master = await SpecService.getMaster(id);
    if (!master || !master.versionHistory) return null;
    const versionEntry = master.versionHistory.find((v) => v.version === targetVersion);
    if (!versionEntry) return null;

    // Archive current state
    const maxVersions = master.maxVersionsKept || 10;
    const snapshot: SpecMasterVersion = {
      version: master.version,
      snapshot: {
        coreContent: JSON.parse(JSON.stringify(master.coreContent)),
        systems: JSON.parse(JSON.stringify(master.systems)),
        title: master.title,
        description: master.description,
      },
      savedBy: userName || master.modifiedBy || 'unknown',
      savedAt: master.lastModified,
      message: `Reverted to v${targetVersion}`,
    };
    const history = [...(master.versionHistory || []), snapshot];
    master.versionHistory = history.slice(-maxVersions);

    // Restore the target version
    master.coreContent = JSON.parse(JSON.stringify(versionEntry.snapshot.coreContent));
    master.systems = JSON.parse(JSON.stringify(versionEntry.snapshot.systems));
    if (versionEntry.snapshot.title) master.title = versionEntry.snapshot.title;
    if (versionEntry.snapshot.description) master.description = versionEntry.snapshot.description;
    master.version = (master.version || 0) + 1;
    master.modifiedBy = userName;
    master.lastModified = new Date().toISOString();

    return await SpecService.savePublished(master);
  },

  /** Get diff between a draft and its published blueprint */
  getDraftDiff: async (draftId: string): Promise<BlockDiff[]> => {
    const draft = await SpecService.getDraft(draftId);
    if (!draft) return [];
    const master = await SpecService.getMaster(draft.blueprintId);
    if (!master) return [];
    return diffBlocks(master.coreContent || [], draft.coreContent || []);
  },

  /* ══ Projects CRUD (delegates to ProjectService) ══ */
  listProjects: async (): Promise<Project[]> => ProjectService.list(),
  getProject: async (id: string): Promise<Project | null> => {
    const all = await ProjectService.list();
    return all.find((p) => p.id === id) || null;
  },
  saveProject: async (project: Project): Promise<Project> => ProjectService.save(project),
  deleteProject: async (id: string): Promise<void> => ProjectService.delete(id),

  /* ══ Spec Assembly (for Forge/Build components) ══ */
  assembleSpec: async (projectId: string, masterId: string): Promise<AssembledSpec | null> => {
    const project = await SpecService.getProject(projectId);
    const master = await SpecService.getMaster(masterId);
    if (!project || !master) return null;
    const sel = project.selections[masterId] || { enabledSystems: [], options: {}, parameters: {} };
    let blocks = [
      ...(master.coreContent || []).map((b) => ({ ...b, source: 'core', included: true })),
    ];
    (master.systems || []).forEach((sys) => {
      const enabled = sel.enabledSystems.includes(sys.id);
      (sys.content || []).forEach((b) => {
        blocks.push({
          ...b,
          source: 'system',
          systemName: sys.name,
          systemId: sys.id,
          included: enabled,
        });
      });
    });
    return { master, blocks, selections: sel };
  },
};

/* ── Expose for legacy browser globals ── */
// Guarded: the pure diff functions in this module are also imported by
// node-side unit tests, where no window exists.
if (typeof window !== 'undefined') {
  (window as any).SpecService = SpecService;
  (window as any).CSI_DIVISIONS = CSI_DIVISIONS;
  (window as any).SYSTEM_CATALOG = SYSTEM_CATALOG;
}
